import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { widthRequiresNonBase } from 'Util/_HumanWritten/WidthUtils';
import { Flex } from 'Views/Components/Flex/Flex';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { VehicleTabActionCreators, wizardDataVehicleTabReducer } from './Reducers/vehicleTabReducer';
import TrailerWidthInput from './TrailerWidthInput';

import { useVehicleTabContext } from './VehicleTabContext';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export default function TrailerWidthDetails() {
	const { towOnTypes, cargoTypes, trailerPrices } = useVehicleTabContext();
	const intl = useIntl();
	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	if (wizardData.innerWizardData.type !== 'vehicle') {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const onChecked = (checked: boolean) => {
		const newData = wizardDataVehicleTabReducer(
			wizardData,
			VehicleTabActionCreators.hasTrailerWidthDetails({ checked }),
		);
		if (newData) {
			setWizardData(newData);
		}

		if (!checked) {
			const newErrors = { ...typedErrors };
			delete newErrors.trailerWidthId;
			setInnerWizardErrors(newErrors);
		}
	};

	// Disable the toggle when a required width must stay applied. The trailer requires a width when
	// its tow-on type's minimum width exceeds the base width. When the vehicle and trailer widths are
	// linked they share one width, so the (synced) checkbox must also stay ticked when the VEHICLE's
	// type requires a width — otherwise unticking here would strip the required width off both.
	const isDisabled = () => {
		if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
			return false;
		}

		const { trailerTypeId, cargoTypeId } = wizardData.innerWizardData;
		const trailerRequiresWidth = widthRequiresNonBase(towOnTypes.find(v => v.id === trailerTypeId)?.minimumWidthId);
		const vehicleRequiresWidth =
			appConfigStore.vehicleAndTrailerWidthLinked &&
			widthRequiresNonBase(cargoTypes.find(v => v.id === cargoTypeId)?.minimumWidthId);

		return trailerRequiresWidth || vehicleRequiresWidth;
	};

	const onChange = (trailerWidthId: string) => {
		const newData = wizardDataVehicleTabReducer(
			wizardData,
			VehicleTabActionCreators.changeTrailerWidth({ widthId: trailerWidthId }),
		);
		if (newData) {
			setWizardData(newData);
		}

		if (trailerWidthId) {
			const newErrors = { ...typedErrors };
			delete newErrors.trailerWidthId;
			setInnerWizardErrors(newErrors);
		}
	};

	const defaultLabel = intl.formatMessage({
		id: 'vehicle-tab__trailer-width-checkbox__label',
		defaultMessage: 'My trailer is narrower/wider than standard',
	});

	// When the vehicle and trailer widths are linked, the trailer follows the vehicle's width
	// rather than having its own. We still show the (synced) checkbox, but instead of a separate
	// width picker we surface an information box explaining the link.
	const widthLinked = appConfigStore.vehicleAndTrailerWidthLinked;

	return (
		<Flex direction="col" flex={1} gap="xs">
			<CheckboxInput
				className="trailer-width-checkbox"
				label={defaultLabel}
				disabled={isDisabled()}
				checked={!!wizardData.innerWizardData.showTrailerWidthDetails}
				onChange={e => onChecked(e.target.checked)}
			/>
			{wizardData.innerWizardData.showTrailerWidthDetails &&
				(widthLinked ? (
					<MessageBox status="info" slim noBottomMargin>
						{intl.formatMessage({
							id: 'vehicle-tab__trailer-width-linked__info',
							defaultMessage:
								'Your trailer width is linked to your vehicle width, so they use the same width.',
						})}
					</MessageBox>
				) : (
					<TrailerWidthInput
						towOnTypes={towOnTypes}
						trailerTypeId={wizardData.innerWizardData.trailerTypeId}
						trailerWidthId={wizardData.innerWizardData.trailerWidthId}
						onChange={onChange}
						prices={trailerPrices}
						isRequired
						errors={typedErrors?.trailerWidthId}
					/>
				))}
		</Flex>
	);
}
